/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';

const BACKEND = process.env.REACT_APP_API_BASE;

export default function CRM() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [filter, setFilter] = useState('all'); // all | active | expiring | none

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${BACKEND}/admin/crm`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setData(json);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const daysUntil = (isoDate) => {
    if (!isoDate) return null;
    const diff = new Date(isoDate) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const statusMeta = {
    active: { label: 'Active', color: '#2D6A4F', bg: '#E8F5EE' },
    trialing: { label: 'Trial', color: '#1565C0', bg: '#E3F2FD' },
    past_due: { label: 'Past Due', color: '#C45000', bg: '#FFF3E0' },
    canceled: { label: 'Cancelled', color: '#9B2335', bg: '#FDE8E8' },
    none: { label: 'No subscription', color: '#888', bg: '#F0EBE4' },
  };

  const filtered = data.filter(u => {
    if (filter === 'active') return u.subscription_status === 'active';
    if (filter === 'expiring') {
      const days = daysUntil(u.subscription_end);
      return days !== null && days <= 30 && days > 0;
    }
    if (filter === 'none') return u.subscription_status === 'none';
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'created_at') return new Date(b.created_at) - new Date(a.created_at);
    if (sortBy === 'screens') return b.screens - a.screens;
    if (sortBy === 'renewal') {
      if (!a.subscription_end) return 1;
      if (!b.subscription_end) return -1;
      return new Date(a.subscription_end) - new Date(b.subscription_end);
    }
    if (sortBy === 'value') return b.monthly_value - a.monthly_value;
    return 0;
  });

  // Summary stats
  const totalMRR = data.filter(u => u.subscription_status === 'active').reduce((a, u) => a + u.monthly_value, 0);
  const activeCount = data.filter(u => u.subscription_status === 'active').length;
  const expiringCount = data.filter(u => { const d = daysUntil(u.subscription_end); return d !== null && d <= 30 && d > 0; }).length;
  const offlineAlerts = data.reduce((a, u) => {
    // count screens offline > 24h
    return a; // simplified
  }, 0);

  if (loading) return <div style={{ padding: 40, color: '#999', textAlign: 'center' }}>Loading CRM data...</div>;
  if (error) return <div style={{ padding: 40, color: '#9B2335' }}>Error: {error}</div>;

  return (
    <div style={{ background: '#F8F6F3', minHeight: '100%' }}>
      {/* Header */}
      <div style={hdr}>
        <h1 style={pageTitle}>CRM</h1>
        <button onClick={load} style={btnGhost}>↻ Refresh</button>
      </div>

      <div style={{ padding: '16px 24px' }}>
        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total customers', value: data.length, color: '#1A1A1A', bg: '#fff' },
            { label: 'Active subscriptions', value: activeCount, color: '#2D6A4F', bg: '#E8F5EE' },
            { label: 'Annual revenue', value: `£${totalMRR}`, color: '#8B1E2D', bg: '#FDF5F6' },
            { label: 'Renewing in 30 days', value: expiringCount, color: expiringCount > 0 ? '#C45000' : '#888', bg: expiringCount > 0 ? '#FFF3E0' : '#fff' },
          ].map(stat => (
            <div key={stat.label} style={{ background: stat.bg, border: '1px solid #E8E3DC', borderRadius: 10, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{stat.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: stat.color }}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* Expiring soon alert */}
        {expiringCount > 0 && (
          <div style={{ background: '#FFF3E0', border: '1px solid #FFCC80', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>⚠️</span>
            <div>
              <strong style={{ fontSize: 13, color: '#C45000' }}>{expiringCount} subscription{expiringCount !== 1 ? 's' : ''} renewing within 30 days</strong>
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Consider reaching out to confirm renewal.</div>
            </div>
          </div>
        )}

        {/* Filters + Sort */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { id: 'all', label: `All (${data.length})` },
              { id: 'active', label: `Active (${activeCount})` },
              { id: 'expiring', label: `Expiring (${expiringCount})` },
              { id: 'none', label: `No sub (${data.filter(u => u.subscription_status === 'none').length})` },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid #E8E3DC', cursor: 'pointer', fontSize: 12, fontWeight: filter === f.id ? 600 : 400, background: filter === f.id ? '#111' : '#fff', color: filter === f.id ? '#fff' : '#555' }}>
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#888' }}>Sort:</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={selectSt}>
              <option value="created_at">Newest first</option>
              <option value="screens">Most screens</option>
              <option value="renewal">Renewal date</option>
              <option value="value">Annual value</option>
            </select>
          </div>
        </div>

        {/* Customer table */}
        <div style={tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E8E3DC' }}>
                <th style={th}>Customer</th>
                <th style={th}>Contact</th>
                <th style={{ ...th, textAlign: 'center' }}>Screens</th>
                <th style={th}>Subscription</th>
                <th style={th}>Renewal</th>
                <th style={{ ...th, textAlign: 'right' }}>Ann. Value</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u, i) => {
                const days = daysUntil(u.subscription_end);
                const meta = statusMeta[u.subscription_status] || statusMeta.none;
                const isExpiringSoon = days !== null && days <= 30 && days > 0;
                const isExpired = days !== null && days <= 0;

                return (
                  <tr key={u.id} style={{ borderBottom: '1px solid #F0EBE4', background: i % 2 === 0 ? '#fff' : '#FAFAF9' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: '#1A1A1A' }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{u.email}</div>
                      <div style={{ fontSize: 10, color: '#ccc', marginTop: 1 }}>
                        Joined {new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </td>
                    <td style={td}>
                      <div style={{ fontSize: 13, color: '#555' }}>{u.phone}</div>
                      {u.stripe_customer_id && (
                        <a href={`https://dashboard.stripe.com/customers/${u.stripe_customer_id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, color: '#8B1E2D', textDecoration: 'none' }}>View in Stripe →</a>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{u.screens}</div>
                      <div style={{ fontSize: 11, color: u.online > 0 ? '#2D6A4F' : '#aaa' }}>
                        {u.online > 0 ? `${u.online} online` : 'all offline'}
                      </div>
                    </td>
                    <td style={td}>
                      <span style={{ background: meta.bg, color: meta.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                        {meta.label}
                      </span>
                    </td>
                    <td style={td}>
                      {u.subscription_end ? (
                        <div>
                          <div style={{ fontWeight: 500, color: isExpired ? '#9B2335' : isExpiringSoon ? '#C45000' : '#1A1A1A' }}>
                            {new Date(u.subscription_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                          <div style={{ fontSize: 11, color: isExpired ? '#9B2335' : isExpiringSoon ? '#C45000' : '#aaa', marginTop: 2 }}>
                            {isExpired ? `${Math.abs(days)}d overdue` : `in ${days} days`}
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#ccc', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'right' }}>
                      {u.monthly_value > 0 ? (
                        <span style={{ fontWeight: 700, color: '#2D6A4F', fontSize: 15 }}>£{u.monthly_value}</span>
                      ) : (
                        <span style={{ color: '#ccc' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>No customers found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const hdr = { background: '#fff', borderBottom: '1px solid #E8E3DC', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 10 };
const pageTitle = { fontSize: 18, fontWeight: 700, color: '#1A1A1A', margin: 0 };
const btnGhost = { background: 'none', color: '#888', border: '1px solid #E8E3DC', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer' };
const selectSt = { padding: '6px 10px', border: '1px solid #E8E3DC', borderRadius: 8, fontSize: 12, cursor: 'pointer', background: '#fff' };
const tableWrap = { background: '#fff', border: '1px solid #E8E3DC', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' };
const th = { padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#888', background: '#FAFAF9', letterSpacing: 0.3 };
const td = { padding: '12px 16px', color: '#555', verticalAlign: 'top' };